import React, { useState, useEffect, useRef, useCallback } from 'react';
import Typo from 'typo-js';
import { Menu, MenuItem, makeStyles } from '@material-ui/core';
import debounce from 'lodash.debounce';

const useStyles = makeStyles((theme) => ({
  inputRoot: {
    minHeight: '50px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    caretColor: 'black',
    border: '1px solid',
    overflow: 'auto',
    color: 'black',
    textAlign: 'left',
  },
  invalidWord: {
    textDecoration: 'underline',
    textDecorationColor: 'red',
    textDecorationStyle: 'wavy',
    // cursor: 'pointer',
  },
  menuPaper: {
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  menuItem: {
    fontSize: '14px',
    '&:hover': { backgroundColor: '#f0f0f0' },
  },
}));

const SpellCheckTextField = ({ value, onChange, placeholder, InputProps = {}, className, style, maxLength, ...props }) => {
  const classes = useStyles();
  const contentEditableRef = useRef(null);
  const dictionary = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedWord, setSelectedWord] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    async function loadDictionary() {
      const affData = await fetch('/dictionaries/en_US.aff').then((res) => res.text());
      const dicData = await fetch('/dictionaries/en_US.dic').then((res) => res.text());
      dictionary.current = new Typo('en_US', affData, dicData);
      checkSpelling(value);
    }
    loadDictionary();
  }, []);

  const setCursorToEnd = (element) => {
    if (!element) return; // Ensure the element exists
   
    const range = document.createRange();
    const selection = window.getSelection();
   
    range.selectNodeContents(element); // Select the entire content
    range.collapse(false); // Collapse range to the end (cursor moves to end)
   
    selection.removeAllRanges(); // Clear previous selections
    selection.addRange(range); // Apply new range
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setCursorToEnd(contentEditableRef.current);
    });
   
    if (contentEditableRef.current) {
      observer.observe(contentEditableRef.current, { childList: true, subtree: true });
    }
   
    return () => observer.disconnect();
  }, [contentEditableRef]);

  useEffect(() => {
    if (dictionary.current) {
      checkSpelling(value);
    }
  }, [value]);

  const debounceCheckSpelling = useCallback(
    debounce((text) => checkSpelling(text), 500),
    []
  );

  const handleChange = (newValue) => {
    if (newValue?.length > maxLength) {
      newValue = newValue.substring(0, maxLength);
    }
    onChange({ target: { value: newValue } });
    debounceCheckSpelling(newValue);
  };

  const checkSpelling = (text) => {
    if (!dictionary.current) return;

    const words = text.split(/(\s+)/);
    const highlightedWords = words.map((word, index) => {
      const trimmed = word.trim();
      if (trimmed && !dictionary.current.check(trimmed)) {
        return `<span class="${classes.invalidWord}" data-index="${index}">${word}</span>`;
      }
      return word;
    });

    const newHtml = highlightedWords.join('');
    contentEditableRef.current.innerHTML = newHtml;
    // if (contentEditableRef.current) {
    //   const selection = window.getSelection();
    //   const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    //   contentEditableRef.current.innerHTML = newHtml;
    //   // Restore cursor position if possible
    //   if (range) {
    //     selection.removeAllRanges();
    //     selection.addRange(range);
    //     contentEditableRef.current.focus();
    //   }
    // }
  };

  const handleInput = (e) => {
    handleChange(e.currentTarget.innerText);
  };

  const handleRightClick = (event) => {
    const selection = window.getSelection();
    const node = selection.anchorNode?.parentNode;

    if (node && node.classList.contains(classes.invalidWord)) {
      event.preventDefault();

      setSelectedNode(node);
      setSelectedWord(node.textContent.trim());
      setSuggestions(dictionary.current.suggest(node.textContent.trim()));
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (selectedNode) {
      const words = value.split(/(\s+)/);
      const index = Array.from(selectedNode.parentNode.childNodes).indexOf(selectedNode);
      let currentIndex = 0;

      for (let i = 0; i < words.length; i++) {
        if (words[i].trim() === selectedWord) {
          words[i] = suggestion;
          break;
        }
      }

      const newValue = words.join('');
      handleChange(newValue);
    }

    handleCloseMenu();
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSuggestions([]);
    setSelectedWord(null);
    setSelectedNode(null);
  };

  return (
    <div style={{width:'100%'}} onContextMenu={handleRightClick}>
      {/* <TextField
        variant="outlined"
        placeholder={placeholder}
        {...props}
        InputProps={{
          ...InputProps,
          classes: {
            ...InputProps.classes,
            root: classes.inputRoot,
          },
          inputComponent: () => (
            <div
              ref={contentEditableRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              className={className}
              style={styles}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          ),
        }}
      /> */}
      <div
        ref={contentEditableRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        className={`${classes.inputRoot} ${className}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: value }}
        
      />

      <Menu
        anchorEl={menuAnchor}
        keepMounted
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        classes={{ paper: classes.menuPaper }}
      >
        {suggestions.length > 0 ? (
          suggestions.map((sug, i) => (
            <MenuItem key={i} onClick={() => handleSuggestionClick(sug)} className={classes.menuItem}>
              {sug}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled className={classes.menuItem}>
            No suggestions
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default SpellCheckTextField;
